"use client"

import { useState, useMemo, useCallback, useRef, useEffect } from "react"
import { useTranslations } from "next-intl"
import type { components } from "umbraco"
import { replaceRichTextLinks } from "utils"
import { getFaqAnswerMarkup, getFaqAnswerText } from "../../common/faqAnswer"
import Container from "../../Container"
import GradientChevron from "../../icons/GradientChevron"
import Plus from "../../icons/Plus"
import Minus from "../../icons/Minus"
import SearchIcon from "../../icons/SearchIcon"
import CloseIcon from "../../icons/CloseIcon"
import ChevronLeft from "../../icons/ChevronLeft"
import ChevronRight from "../../icons/ChevronRight"
import { NoResultsIllustration } from "../../layout/SearchPage/icons"
import {
  Root,
  TitleRow,
  Title,
  ChevronWrapper,
  SearchBarWrapper,
  SearchInput,
  SearchIconButton,
  ClearButton,
  FAQList,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  HoverChevronWrapper,
  QuestionText,
  AnswerText,
  AnswerRichText,
  IconWrapper,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  ResetButton,
  PaginationWrapper,
  PaginationArrow,
  PaginationDot,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type InnerFAQsData = components["schemas"]["InnerFaqsPropertiesModel"]
type FaqItemData = components["schemas"]["FaqItemPropertiesModel"]

interface Props {
  data: InnerFAQsData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

const ITEMS_PER_PAGE = 6

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function InnerFAQs({ data, locale }: Props) {
  const [expanded, setExpanded] = useState<string | false>(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const rootRef = useRef<HTMLDivElement>(null)
  const isFirstRender = useRef(true)
  const t = useTranslations("InnerPages")

  // EGDE-329: switching FAQ page leaves the viewport scrolled down at the
  // pagination, so the new page's first questions sit above the fold. Bring the
  // top of the FAQ section back into view (scroll-margin-top clears the header).
  // This MUST run after the new page has rendered — scrolling synchronously from
  // the click handler targets the stale (previous-page) layout and lands wrong,
  // so it runs in an effect keyed on the page, skipping the initial mount.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    // Runs after the new (shorter) page has committed and the browser has
    // settled its scroll clamp, so scrollIntoView targets the correct position.
    // scroll-margin-top on the Root keeps the heading clear of the fixed header.
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [currentPage])

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  const allItems = data.items?.items ?? []

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems
    const query = searchQuery.trim().toLowerCase()
    return allItems.filter((block) => {
      const props = block.content.properties as FaqItemData | undefined
      const question = props?.question?.toLowerCase() ?? ""
      const answer = getFaqAnswerText(props?.answer).toLowerCase()
      return question.includes(query) || answer.includes(query)
    })
  }, [allItems, searchQuery])

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const visibleItems = filteredItems.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
      setCurrentPage(1)
      setExpanded(false)
    },
    [],
  )

  const handleClearSearch = useCallback(() => {
    setSearchQuery("")
    setCurrentPage(1)
    setExpanded(false)
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
    setExpanded(false)
  }, [])

  // RTL: right arrow goes to previous page, left arrow goes to next page
  const handleRightArrow = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
    setExpanded(false)
  }, [])

  const handleLeftArrow = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
    setExpanded(false)
  }, [totalPages])

  const rightDisabled = currentPage <= 1
  const leftDisabled = currentPage >= totalPages

  return (
    <Container paddingMobile>
      <Root ref={rootRef}>
        {/* Title row */}
        <TitleRow>
          <ChevronWrapper>
            <GradientChevron />
          </ChevronWrapper>
          <Title variant="h2">{data.title}</Title>
        </TitleRow>

        {/* Search bar */}
        <SearchBarWrapper>
          <SearchIconButton aria-label={t("faq")} tabIndex={-1}>
            <SearchIcon width={19} height={19} />
          </SearchIconButton>
          <SearchInput
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={t("hereForYou")}
            aria-label={t("faq")}
          />
          {searchQuery && (
            <ClearButton onClick={handleClearSearch} aria-label="clear">
              <CloseIcon width={20} height={20} />
            </ClearButton>
          )}
        </SearchBarWrapper>

        {/* FAQ accordion list */}
        {visibleItems.length > 0 ? (
          <FAQList>
            {visibleItems.map((block, index) => {
              const props = block.content.properties as
                | FaqItemData
                | undefined
              const panelId = `inner-faq-panel-${block.content.id ?? index}`
              const isExpanded = expanded === panelId

              return (
                <StyledAccordion
                  key={block.content.id ?? index}
                  expanded={isExpanded}
                  onChange={handleChange(panelId)}
                  disableGutters
                  square
                >
                  <StyledAccordionSummary
                    expandIcon={
                      <IconWrapper>
                        {isExpanded ? <Minus /> : <Plus />}
                      </IconWrapper>
                    }
                  >
                    <HoverChevronWrapper>
                      <GradientChevron width={12} height={20} />
                    </HoverChevronWrapper>
                    <QuestionText variant="body1" $expanded={isExpanded}>
                      {props?.question}
                    </QuestionText>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    {(() => {
                      const markup = getFaqAnswerMarkup(props?.answer)
                      return markup ? (
                        <AnswerRichText
                          dangerouslySetInnerHTML={{
                            __html: replaceRichTextLinks(markup),
                          }}
                        />
                      ) : (
                        <AnswerText variant="body2">
                          {getFaqAnswerText(props?.answer)}
                        </AnswerText>
                      )
                    })()}
                  </StyledAccordionDetails>
                </StyledAccordion>
              )
            })}
          </FAQList>
        ) : (
          <EmptyState>
            <EmptyStateIcon aria-hidden="true">
              <NoResultsIllustration />
            </EmptyStateIcon>
            <EmptyStateTitle variant="body1">
              {t("noAnswerFound")}
            </EmptyStateTitle>
            <ResetButton type="button" onClick={handleClearSearch}>
              {t("resetSearch")}
            </ResetButton>
          </EmptyState>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationWrapper>
            <PaginationArrow
              onClick={handleRightArrow}
              disabled={rightDisabled}
              aria-label="Previous page"
            >
              <ChevronRight />
            </PaginationArrow>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <PaginationDot
                  key={page}
                  $active={page === currentPage}
                  onClick={() => handlePageChange(page)}
                  aria-label={`Page ${page}`}
                  aria-current={page === currentPage ? "page" : undefined}
                >
                  {page}
                </PaginationDot>
              ),
            )}
            <PaginationArrow
              onClick={handleLeftArrow}
              disabled={leftDisabled}
              aria-label="Next page"
            >
              <ChevronLeft />
            </PaginationArrow>
          </PaginationWrapper>
        )}
      </Root>
    </Container>
  )
}
